import React, { useEffect, useMemo, useRef, useState } from 'react';
import useStore from '../store/useStore';
import { reportsAPI } from '../services/api';
import PeriodSelect from './common/PeriodSelect';
import '../styles/DashboardFilters.css';

const VALUE_LIMIT = 500;

/** A filter's dropdown: search, tick the values to keep, clear. Values load when it first opens. */
function FilterDropdown({ companyId, member, label, values, onChange }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open || options) return undefined;
    let live = true;
    reportsAPI.query(companyId, { dimensions: [member], limit: VALUE_LIMIT, order: { [member]: 'asc' } })
      .then((res) => {
        if (!live) return;
        if (!res?.success) throw new Error(res?.error || 'Could not load the values');
        setOptions([...new Set((res.data?.data || []).map((r) => r[member]).filter((v) => v !== null && v !== undefined && v !== '').map(String))]);
      })
      .catch((err) => { if (live) setError(err?.response?.data?.error || err.message || 'Could not load the values'); });
    return () => { live = false; };
  }, [open, options, companyId, member]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [open]);

  const chosen = new Set(values);
  const summary = values.length === 0 ? 'All' : values.length === 1 ? values[0] : `${values.length} selected`;
  const term = search.trim().toLowerCase();
  const shown = (options || []).filter((v) => !term || v.toLowerCase().includes(term));
  const toggle = (v) => onChange(chosen.has(v) ? values.filter((x) => x !== v) : [...values, v]);

  return (
    <div className={`dash-filter ${values.length ? 'is-active' : ''}`} ref={ref}>
      <button type="button" className="dash-filter-button" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        aria-label={`${label}: ${summary}`}>
        <span className="dash-filter-label">{label}</span>
        <span className="dash-filter-value">{summary}</span>
        <span aria-hidden="true" className="dash-filter-caret">▾</span>
      </button>
      {open && (
        <div className="dash-filter-pop" role="dialog" aria-label={`Filter by ${label}`}>
          <input type="search" className="dash-filter-search" placeholder={`Search ${label.toLowerCase()}`} aria-label={`Search ${label}`}
            value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          <div className="dash-filter-options" role="group" aria-label={`${label} values`}>
            {error && <p className="dash-filter-note dash-filter-note--error">{error}</p>}
            {!error && !options && <p className="dash-filter-note">Loading…</p>}
            {options && shown.length === 0 && <p className="dash-filter-note">{options.length ? 'No values match.' : 'No values found.'}</p>}
            {shown.map((v) => (
              <label key={v} className="dash-filter-option">
                <input type="checkbox" checked={chosen.has(v)} onChange={() => toggle(v)} />
                <span>{v}</span>
              </label>
            ))}
            {options && options.length >= VALUE_LIMIT && <p className="dash-filter-note">Showing the first {VALUE_LIMIT} values. Search to narrow them down.</p>}
          </div>
          <div className="dash-filter-actions">
            <button type="button" className="btn-secondary-small" onClick={() => onChange([])} disabled={!values.length}>Clear</button>
            <button type="button" className="btn-primary-small" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The bar above a dashboard's widgets: its period and its filters. The
 * choices apply to this visit only. Filters set by clicking a chart show as
 * chips here so they can be seen and removed.
 */
const DashboardFilterBar = ({ dashboard }) => {
  const companyId = useStore((state) => state.currentCompany?.id);
  const period = useStore((state) => state.dashboardDateRange);
  const setPeriod = useStore((state) => state.setDashboardDateRange);
  const active = useStore((state) => state.dashboardFilters);
  const setFilter = useStore((state) => state.setDashboardFilter);
  const clearFilters = useStore((state) => state.clearDashboardFilters);
  const configured = useMemo(() => (Array.isArray(dashboard?.filters) ? dashboard.filters : []), [dashboard?.filters]);
  const defaultPeriod = dashboard?.default_date_range || null;

  const configuredMembers = new Set(configured.map((f) => f.member));
  const fromCharts = Object.entries(active).filter(([member]) => !configuredMembers.has(member));
  const anyChange = Object.keys(active).length > 0 || (period || null) !== defaultPeriod;

  return (
    <div className="dash-filter-bar" role="region" aria-label="Dashboard filters">
      <div className="dash-filter-period">
        <span>Period</span>
        <PeriodSelect ariaLabel="Dashboard period" value={period || ''} onChange={(v) => setPeriod(v || null)}
          emptyLabel="Each report's own period" ranges="calendar-financial" />
      </div>
      {configured.map((f) => (
        <FilterDropdown key={f.member} companyId={companyId} member={f.member} label={f.label}
          values={active[f.member]?.values || []} onChange={(values) => setFilter(f.member, values, { label: f.label })} />
      ))}
      {fromCharts.map(([member, f]) => (
        <span key={member} className="dash-filter-chip">
          <span>{f.label} is {f.values.join(', ')}</span>
          <button type="button" aria-label={`Remove the filter ${f.label} is ${f.values.join(', ')}`} onClick={() => setFilter(member, [])}>×</button>
        </span>
      ))}
      {anyChange && (
        <button type="button" className="dash-filter-reset" onClick={() => { clearFilters(); setPeriod(defaultPeriod); }}>
          Reset
        </button>
      )}
    </div>
  );
};

export default DashboardFilterBar;
